import type {
  IncomingFluencyResponse,
  IncomingLearningRecord,
  Rejection,
} from "@bindery/convex-reader/submission";
import type { ResponseId } from "@bindery/shared/contracts/fluency";
import type { Reading } from "@bindery/shared/reader/cdn";
import type { VoiceFollowPageSegment } from "@bindery/shared/contracts/voice-follow-log";
import { Dexie, type DexieOptions, type Table } from "dexie";
import { enrollmentKey, type EnrollmentMark } from "./enrollment";
import { clearStrayWordArms } from "./stray-word-arms";

export const READER_DB_NAME = "rbe-reader";

interface Queued {
  readerId: string;
  queuedAt: number;
  attempts: number;
  /** Sprint 3's column; the reader route never sets it. */
  orientationSessionId?: string;
}

export type RecordState = "provisional" | "pending";

export type OutboxRecordRow = IncomingLearningRecord &
  Queued & { state: RecordState };

export type OutboxResponseRow = IncomingFluencyResponse & Queued;

export interface BookmarkValue {
  readingId: string;
  currentPage: number;
  /** Epoch ms, already corrected for clock skew. */
  lastupdate: number;
  completed?: boolean;
  stars?: number;
}

export type BookmarkRow = BookmarkValue & { readerId: string };

export type OutboxBookmarkRow = BookmarkRow & {
  queuedAt: number;
  state: "pending" | "skipped";
};

export type QuarantineKind = "record" | "response" | "bookmark";

export type QuarantineReason =
  | Rejection["reason"]
  | "invalid_argument"
  | "server_refused"
  | "bad_bookmark";

export interface QuarantineRow {
  id?: number;
  readerId: string;
  kind: QuarantineKind;
  payload: unknown;
  field: string;
  reason: QuarantineReason;
  quarantinedAt: number;
}

export interface FluencyRow {
  readerId: string;
  /** Normalized by `extractNormalizedWord`, as the server keys it. */
  word: string;
  serverHistory: ResponseId[];
  /** `serverHistory` with this word's pending responses replayed. */
  history: ResponseId[];
  lastUpdated: number;
  /** A server answer for the word has been merged; local appends never set it. */
  serverMerged?: true;
}

/** An enrolled reader's own copy of a reading, as the personalised route served it. */
export interface PersonalReadingRow {
  readerId: string;
  readingId: string;
  reading: Reading;
  /** The route's `x-bridge-set-version`, present on every record from this reading. */
  bridgeSetVersion?: string;
  /** The route's `x-personal-reading-served-at`, also on every record from it. */
  routeServedAt?: number;
  servedAt: number;
}

/**
 * A voice-follow segment waiting for convex-reader, written only for a
 * reader whose parent chose voice research. `pendingDetail` counts its
 * detail batches not yet stored or dropped; it is sent once that is 0.
 */
export interface VoiceSegmentRow {
  id: string;
  readerId: string;
  queuedAt: number;
  segment: Omit<VoiceFollowPageSegment, "detailRefs">;
  detailRefs: string[];
  pendingDetail: number;
}

/** One NDJSON detail batch of a queued segment, for the reader Worker. */
export interface VoiceDetailRow {
  id: string;
  readerId: string;
  segmentId: string;
  queuedAt: number;
  body: string;
  attempts: number;
}

export interface MetaRow {
  key: string;
  value: unknown;
}

export type ReaderDb = Dexie & {
  outboxRecords: Table<OutboxRecordRow, string>;
  outboxResponses: Table<OutboxResponseRow, string>;
  outboxBookmarks: Table<OutboxBookmarkRow, [string, string]>;
  quarantine: Table<QuarantineRow, number>;
  fluency: Table<FluencyRow, [string, string]>;
  bookmarks: Table<BookmarkRow, [string, string]>;
  personalReadings: Table<PersonalReadingRow, [string, string]>;
  voiceSegments: Table<VoiceSegmentRow, string>;
  voiceDetail: Table<VoiceDetailRow, string>;
  meta: Table<MetaRow, string>;
};

// Every compound key leads with readerId: a classroom device switches
// readers, and a row must only ever be read back for the reader it was
// written under.
export function openReaderDb(
  options?: DexieOptions,
  name = READER_DB_NAME,
): ReaderDb {
  const db = new Dexie(name, options) as ReaderDb;
  db.version(1).stores({
    outboxRecords: "clientRecordId, [readerId+queuedAt], state",
    outboxResponses: "clientResponseId, [readerId+queuedAt], [readerId+word]",
    outboxBookmarks: "[readerId+readingId], queuedAt",
    quarantine: "++id, [readerId+quarantinedAt]",
    fluency: "[readerId+word], [readerId+lastUpdated]",
    bookmarks: "[readerId+readingId], [readerId+lastupdate]",
    meta: "key",
  });
  db.version(2).stores({ wordArms: "[readerId+word]" });
  db.version(3).stores({
    voiceSegments: "id, [readerId+queuedAt]",
    voiceDetail: "id, segmentId, [readerId+queuedAt]",
  });
  // The arm mirror retires: the personalised route serves each word's
  // treatment. A reader it held arms for was enrolled, which the fallback
  // rule still needs to know offline.
  db.version(4)
    .stores({
      wordArms: null,
      personalReadings: "[readerId+readingId], [readerId+servedAt]",
    })
    .upgrade(async (tx) => {
      const readerIds = new Set<string>();
      await tx
        .table<{ readerId: string }>("wordArms")
        .each((row) => void readerIds.add(row.readerId));
      const heardAt = Date.now();
      await tx.table<MetaRow, string>("meta").bulkPut(
        [...readerIds].map((readerId) => ({
          key: enrollmentKey(readerId),
          value: { enrolled: true, heardAt } satisfies EnrollmentMark,
        })),
      );
    });
  // Sticky, so every open empties it, not only a tab's first: an old tab
  // can add the table back between this tab's connections.
  db.on("ready", () => clearStrayWordArms(db).catch(() => undefined), true);
  return db;
}

/** Bounds for a compound index whose first part is `readerId`. */
export function readerRange(readerId: string): [unknown[], unknown[]] {
  return [
    [readerId, Dexie.minKey],
    [readerId, Dexie.maxKey],
  ];
}

export async function readMeta<T>(
  db: ReaderDb,
  key: string,
): Promise<T | undefined> {
  const row = await db.meta.get(key);
  return row?.value as T | undefined;
}

export async function writeMeta(
  db: ReaderDb,
  key: string,
  value: unknown,
): Promise<void> {
  await db.meta.put({ key, value });
}
